import {useGetResourceEvents} from '@/modules/system-logs/api'
import {mapOldStateToEvents, mapOldValuesToLogPatches, type ValueChange} from '@/modules/system-logs/functions'
import {useEventLogContext} from '@/shared/contexts/event-log'
import {renderDetailValue} from './details-renderer'
import {Badge, Box, Flex, Table, Text} from '@chakra-ui/react'
import {flattenValue, isNestedValue, toPascalCase} from '@cypro/ui/utils'
import {useTranslate} from '@tolgee/react'
import {type ReactNode, useMemo} from 'react'

type ChangeRowMode = 'comparison' | 'simple'

export function LogDrawerChangeDetails() {
  const {t} = useTranslate('common')
  const {log} = useEventLogContext()
  const [previousLogs] = useGetResourceEvents({resourceId: log.resourceId || '', performedBefore: log.performedAt})

  const changesList = useMemo(() => {
    const mappedEvents = mapOldStateToEvents(previousLogs)
    return mapOldValuesToLogPatches(log, mappedEvents)
  }, [log, previousLogs])

  return (
    <Flex paddingTop={4} gap={5} direction="column">
      <Text fontSize="md" fontWeight="semibold">
        {t('change_details')}
      </Text>
      {log.patches ? (
        <Box>
          {log.action === 'update' ? (
            <ValueComparisonTable changesList={changesList} />
          ) : (
            <SimpleTable changesList={changesList} />
          )}
        </Box>
      ) : (
        <Text>{t('no_details_are_available')}</Text>
      )}
    </Flex>
  )
}

// Field names are camelCase; a path with a space is already a readable label (e.g. "PR-20261019-1001 Carboplatin").
function formatPath(path: string) {
  return path.includes(' ') ? path : toPascalCase(path)
}

function ChangeRow({path, oldValue, newValue, mode}: {path: string; oldValue?: unknown; newValue?: unknown; mode: ChangeRowMode}) {
  const renderValue = (value: unknown): ReactNode => {
    if (value === undefined) return '-'
    return renderDetailValue(path, value)
  }

  if (mode === 'comparison') {
    return (
      <Table.Row>
        <Table.Cell border="none" color="fg.muted" fontSize="xs">
          <Badge size="lg">{formatPath(path)}</Badge>
        </Table.Cell>
        <Table.Cell bg="red.subtle" color="fg.muted" border="none" textDecoration="line-through" textAlign="right">
          {renderValue(oldValue)}
        </Table.Cell>
        <Table.Cell bg="green.subtle" color="fg.muted" border="none" overflowWrap="anywhere" textAlign="right">
          {renderValue(newValue)}
        </Table.Cell>
      </Table.Row>
    )
  }

  return (
    <Table.Row color="fg.muted">
      <Table.Cell border="none" p="0" pt="3" display="flex" alignItems="start">
        <Badge size="lg">{formatPath(path)}</Badge>
      </Table.Cell>
      {newValue !== undefined && (
        <Table.Cell border="none" textAlign="right" overflowWrap="anywhere">
          {renderValue(newValue)}
        </Table.Cell>
      )}
    </Table.Row>
  )
}

function ValueComparisonTable({changesList}: {changesList: ValueChange[]}) {
  const {t} = useTranslate('common')

  return (
    <Table.Root>
      <Table.Header>
        <Table.Row borderBottom="2px solid" borderColor="gray.muted">
          <Table.ColumnHeader bg="white">{t('field')}</Table.ColumnHeader>
          <Table.ColumnHeader bg="red.subtle" color="fg.error">
            {t('old_value')}
          </Table.ColumnHeader>
          <Table.ColumnHeader bg="green.subtle" color="fg.success">
            {t('new_value')}
          </Table.ColumnHeader>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {changesList?.map(change => {
          const isNested = isNestedValue(change.value)
          const isOldNested = isNestedValue(change.oldValue)

          if (isNested || isOldNested) {
            const newRows = isNested ? flattenValue(change.value, change.path) : []
            const oldRows = isOldNested ? flattenValue(change.oldValue, change.path) : []
            // merge by path so old/new values align
            const paths = Array.from(new Set([...newRows.map(r => r.path), ...oldRows.map(r => r.path)]))

            return paths.map(path => {
              const newEntry = newRows.find(r => r.path === path)
              const oldEntry = oldRows.find(r => r.path === path)
              return (
                <ChangeRow
                  key={path}
                  path={path}
                  oldValue={oldEntry?.value}
                  newValue={newEntry?.value}
                  mode="comparison"
                />
              )
            })
          }

          return (
            <ChangeRow
              key={change.path + change.value}
              path={change.path}
              oldValue={change.oldValue}
              newValue={change.value}
              mode="comparison"
            />
          )
        })}
      </Table.Body>
    </Table.Root>
  )
}

function SimpleTable({changesList}: {changesList: ValueChange[]}) {
  const {t} = useTranslate('common')
  return (
    <Table.Root>
      <Table.Header>
        <Table.Row borderBottom="2px solid" borderColor="gray.muted" textTransform="capitalize">
          <Table.ColumnHeader bg="white">{t('parameter')}</Table.ColumnHeader>
          <Table.ColumnHeader bg="white" textAlign="right">
            {t('value')}
          </Table.ColumnHeader>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {changesList?.map(change => {
          if (isNestedValue(change.value)) {
            return flattenValue(change.value, change.path).map(row => (
              <ChangeRow key={row.path} path={row.path} newValue={row.value} mode="simple" />
            ))
          }

          return (
            <ChangeRow
              key={change.path + change.value}
              path={change.path}
              newValue={change.value}
              mode="simple"
            />
          )
        })}
      </Table.Body>
    </Table.Root>
  )
}
